import {
  useCancelPreorderMutation,
  useCancelSetMutation,
  useGatherCardMutation,
  useGetMyGatheredQuery,
  useGetMyPreordersQuery,
  usePreorderSetMutation,
} from "./printsApi";
import type { GatheredSet, PrintPreorder } from "./printsApi";
import type { PrintEdition } from "./printProducts";
import { useSignedIn } from "./usePreorders";
import { askPromise } from "./promise/promiseStore";
import { gatherAsk, removeAsk, removeSetAsk, setAsk } from "./promise/promiseCopy";

// The member's gathered trading cards. Cards aren't pre-ordered one at a time: a member gathers a
// day's card (one per day, a promise like a pre-order, confirmed in the modal), and once they hold
// 21 they pre-order all of them as a set. The day's author can also claim its original card, which
// counts as one of them. Putting a card back, or removing a set, is final.

export const SET_SIZE = 21;

export interface Gathering {
  signedIn: boolean;
  /** The member's cards and removals have loaded. */
  ready: boolean;
  /** Gathered and not in a set yet. */
  cards: PrintPreorder[];
  sets: GatheredSet[];
  /** Cards needed for a set. */
  needed: number;
  held: (number: number, edition: PrintEdition) => PrintPreorder | undefined;
  locked: (number: number, edition: PrintEdition) => boolean;
  gather: (number: number, edition: PrintEdition) => Promise<boolean>;
  putBack: (card: PrintPreorder) => Promise<boolean>;
  preorderSet: () => Promise<boolean>;
  removeSet: (set: GatheredSet) => Promise<boolean>;
  busy: boolean;
  error: unknown;
}

export function useGathering(): Gathering {
  const signedIn = useSignedIn();
  const { data } = useGetMyGatheredQuery(undefined, { skip: !signedIn });
  const { data: mine } = useGetMyPreordersQuery(undefined, { skip: !signedIn });
  const [gatherCard, gathering] = useGatherCardMutation();
  const [cancel, cancelling] = useCancelPreorderMutation();
  const [makeSet, making] = usePreorderSetMutation();
  const [cancelSet, removing] = useCancelSetMutation();

  const cards = data?.cards ?? [];
  const sets = data?.sets ?? [];
  const needed = data?.needed ?? SET_SIZE;
  const all = [...cards, ...sets.flatMap((s) => s.cards)];

  return {
    signedIn,
    ready: data !== undefined && mine !== undefined,
    cards,
    sets,
    needed,
    held: (n, e) => all.find((c) => c.number === n && c.edition === e),
    locked: (n, e) => (mine?.locked ?? []).some((l) => l.number === n && l.format === "trading-card" && l.edition === e),
    gather: async (n, e) => {
      if (!(await askPromise(gatherAsk(n, e, needed)))) return false;
      const res = await gatherCard({ number: n, edition: e, confirm: true });
      return !("error" in res);
    },
    putBack: async (card) => {
      if (!(await askPromise(removeAsk(card.number, "trading-card", card.edition)))) return false;
      await cancel(card.id);
      return true;
    },
    preorderSet: async () => {
      if (cards.length < needed || !(await askPromise(setAsk(cards.length)))) return false;
      const res = await makeSet();
      return !("error" in res);
    },
    removeSet: async (set) => {
      if (!(await askPromise(removeSetAsk(set.cardCount)))) return false;
      await cancelSet(set.id);
      return true;
    },
    busy: gathering.isLoading || cancelling.isLoading || making.isLoading || removing.isLoading,
    error: gathering.error || cancelling.error || making.error || removing.error,
  };
}
